import { z } from 'zod';
import { isoDateTime, wireDecimal, type DecimalString } from './common';
import type { OrganizationType } from './auth';
import type { TerminalPermissions } from './terminals';
import { hasFeature } from '@/lib/business-profile';

/**
 * Checkout wire contract (V1-04) — POST {pos}/orders ONLY. The queue/list
 * order domain belongs to V1-05 (src/types/orders.ts); this module owns the
 * CREATE payload and its 201 response.
 *
 * Verified against:
 * - pos-api/app/Http/Controllers/Api/POS/OrderController.php:24-163
 *   (validation :26-43; proxy body :57-100; 201 envelope :139-143;
 *   400 business envelope :158-161; 422 is the STANDARD Laravel envelope
 *   because validate() runs before the try block)
 * - salesgroundai/app/Http/Controllers/API/Internal/OrderController.php:28-97
 * - salesgroundai/app/Services/OrderCreationService.php:30-113,308-319,380-431
 *
 * Round-trip facts encoded in the payload type:
 * - `items[].product_id` is the sellable row id — for a variant, the VARIANT
 *   row's id (`variant_id` is validated upstream but NEVER read by the
 *   service, which prices `item.id` only — OrderCreationService.php:502).
 * - `items[].parent_key` is the STRING array index of the parent item in the
 *   same `items` array (groupItemsByParent, OrderCreationService.php:440-458).
 * - `items[].attributes` persists verbatim on the order item
 *   (OrderCreationService.php:386,424) — item notes ride as
 *   `attributes.notes` because a top-level `items[].notes` is DISCARDED at
 *   the internal validation boundary (Internal/OrderController.php:37-44
 *   omits it from the rules, so validate() strips it).
 * - top-level `notes` IS persisted (OrderCreationService.php:77).
 * - `discount_amount` applies only when no quantity/site/coupon discount
 *   fired, clamped to min(amount, subtotal) (OrderCreationService.php:308-317).
 *   `discount_meta` is deliberately NOT sent: when omitted the server stores
 *   its own richer `{discount_type: "pos_manual", …}` default.
 * - All prices/totals are SERVER-computed; the 201 `order` is authoritative.
 */

/** Exactly the accepted wire values (`in:pickup,delivery,dinein` — no underscore). */
export const ORDER_TYPE_WIRE_VALUES = ['dinein', 'pickup', 'delivery'] as const;
export type OrderTypeWire = (typeof ORDER_TYPE_WIRE_VALUES)[number];

/** One selectable order type and the word this trade uses for it. */
export interface OrderTypeOption {
  value: OrderTypeWire;
  label: string;
}

/**
 * Which order types an organization may OFFER, and what each one is called
 * (V2.2 §4.2/§4.4 — the type side of the restaurant/ecommerce split).
 *
 * The server matrix is not organization-type aware: it keys every rule off
 * `order_type` alone (pos-api/app/Http/Controllers/Api/POS/OrderController.php:88-104),
 * so all three wire values are acceptable to it in any tenant. What the
 * organization type decides is which of them the counter may put in front of a
 * cashier, and the vocabulary:
 *
 *   restaurant  Dine-in · Pickup · Delivery
 *   ecommerce   Take now · Ship to customer — the same two wire values under the words a
 *               shop uses. No dine-in: there is no floor to seat anyone on.
 *   null        Pickup · Delivery, and never dine-in.
 *
 * The null row is the load-bearing one. A missing type is the backend declining
 * to say, which 07-roles-permissions.md §4 requires be read as "assume nothing"
 * — so it gets the two types that exist in every mode and never the one that
 * presumes a floor plan, tables, covers and rounds. That is the same rule
 * src/features/auth/routing.ts applies to the table ROUTES; offering dine-in to
 * an organization that may have no tables is the register's version of the same
 * leak. `quotation` lands in the same row because 03-pos-shell.md declares it
 * unsupported: a neutral pair beats a guess.
 *
 * Terminal restrictions then subtract. Ecommerce keeps an empty list when
 * everything is disabled; a saved shipping restriction must not re-enable
 * itself through a fallback. Other businesses keep their existing fallback.
 * These flags configure the register UI, not order API authorization.
 */
export function orderTypeOptionsFor(
  organizationType: OrganizationType | null,
  permissions?: Partial<TerminalPermissions> | null,
): OrderTypeOption[] {
  const base: OrderTypeOption[] =
    organizationType === 'restaurant'
      ? [
          { value: 'dinein', label: 'Dine-in' },
          { value: 'pickup', label: 'Pickup' },
          { value: 'delivery', label: 'Delivery' },
        ]
      : organizationType === 'ecommerce'
        ? [
            { value: 'pickup', label: 'Take now' },
            { value: 'delivery', label: 'Ship to customer' },
          ]
        : [
            { value: 'pickup', label: 'Pickup' },
            { value: 'delivery', label: 'Delivery' },
          ];

  const allowed = base.filter((option) => {
    if (option.value === 'dinein') return !permissions?.restrict_dinein_orders;
    if (option.value === 'pickup') return !permissions?.restrict_pickup_orders;
    return !permissions?.restrict_delivery_orders;
  });
  return organizationType === 'ecommerce' || allowed.length > 0 ? allowed : base;
}

/**
 * Fields the register must collect for an order type, as ONE table.
 *
 * The rules themselves are enforced twice on purpose — CartStore.fieldIssues
 * refuses to send, and OrderController re-checks on arrival — but the two must
 * be describing the same matrix, and until now nothing said what that matrix
 * was outside a prose docblock. This is that statement, and it is what a test
 * outside the register can assert against.
 *
 * `email` appears nowhere, in any row. The counter never asks for one: a
 * queue that types an address into a phone field will type anything into an
 * email field, and V2.2 removed it rather than collect a fiction.
 */
export type OrderField =
  | 'table_id'
  | 'customer_name'
  | 'customer_phone'
  | 'street'
  | 'area_id'
  /**
   * A retail zone rate — the OTHER thing a delivery can be priced by.
   *
   * Not a fifth delivery field: it is `area_id`'s alternative, and exactly one
   * of the pair is ever collected. See `deliveryPricingFor` below.
   */
  | 'shipping_method_id';

export const ORDER_TYPE_REQUIRED_FIELDS: Record<OrderTypeWire, readonly OrderField[]> = {
  // A tab without a table is an order nobody can find. Covers are optional —
  // a head count is useful, not a precondition for feeding anyone.
  dinein: ['table_id'],
  // A blank pickup is a real walk-in. Demanding a phone here is exactly what
  // pushed counters to a dummy number that merged every unrelated guest.
  pickup: [],
  // All four, because the order cannot be delivered or priced without them.
  delivery: ['customer_name', 'customer_phone', 'street', 'area_id'],
};

/** Fields an order type must NOT carry — the server answers `prohibited`. */
export const ORDER_TYPE_FORBIDDEN_FIELDS: Record<OrderTypeWire, readonly OrderField[]> = {
  // No customer identity at all: a table is not a person, and inventing one
  // pollutes every customer total in the office.
  dinein: ['customer_name', 'customer_phone', 'street', 'area_id'],
  pickup: ['table_id', 'street', 'area_id'],
  delivery: ['table_id'],
};

// ---------------------------------------------------------------------------
// What prices a delivery — and why the two tables above did not move
// ---------------------------------------------------------------------------

/**
 * How this organization costs a `delivery` order.
 *
 *   'area'             a branch delivery radius with one flat charge per area
 *                      (AreaBranch, GET {pos}/area-branches, src/api/areas.ts).
 *   'shipping-method'  a tenant-wide shipping zone rate — flat, or banded by
 *                      quantity (ShippingZoneMethod, GET {pos}/shipping-zones,
 *                      src/api/operations.ts:51).
 */
export type DeliveryPricing = 'area' | 'shipping-method';

/**
 * THE BUG THIS EXISTS TO CLOSE.
 *
 * `ORDER_TYPE_REQUIRED_FIELDS.delivery` above lists `area_id` on every
 * organization type, and TicketPane enforced it on every organization type. But
 * a retail organization has delivery areas hidden from its whole console —
 * `hasFeature(type, 'delivery-areas')` is restaurant/neutral only
 * (src/lib/business-profile.ts:127), and the office nav drops the screen that
 * administers them (src/app/layouts/OfficeShell.tsx:200-205). So a shop could not
 * finish a "Shipping" sale at all: the one thing standing between the cashier
 * and the money was a field the product had deliberately taken away from them.
 * A hidden prerequisite is worse than a visible one, because there is nothing
 * on screen to act on.
 *
 * Retail's model was already there and already priced — server-side,
 * salesgroundai/app/Services/OrderCreationService.php:696-716 resolves
 * `shipping_method_id` to a ShippingZoneMethod whose flat rate or quantity tier
 * OVERRIDES the area charge, and pos-api now validates and forwards the key on
 * both POST /orders and POST /orders/quote. Nothing new is being invented here;
 * the console simply never reached a calculation the core has had all along.
 *
 * Gated on `hasFeature`, never on `type === 'ecommerce'`. business-profile.ts
 * exists precisely because that comparison had been spelled out in six places
 * and the one that was missed failed silently towards the restaurant screen.
 */
export function deliveryPricingFor(type: OrganizationType | null | undefined): DeliveryPricing {
  // Restaurant AND neutral keep areas. Neutral is the load-bearing half: an
  // absent type is the backend declining to say, and `delivery` is offered in
  // every mode, so the mode that must assume nothing keeps the pricing input it
  // has always had rather than being handed a retail-only one.
  if (hasFeature(type, 'delivery-areas')) return 'area';
  return hasFeature(type, 'shipping-zones') ? 'shipping-method' : 'area';
}

/**
 * The required-field row for one order type, once the pricing model is known.
 *
 * WHY THIS IS A FUNCTION AND THE TABLE ABOVE IS UNTOUCHED. The two constants
 * are the contract as the SERVER states it for a restaurant, and
 * src/types/__tests__/checkout.test.ts:84-95 asserts them literally, down to
 * array order. That test is right to: pos-api's per-type matrix
 * (OrderController.php:115-168) is still keyed off `order_type` alone and knows
 * nothing about organization type, and `area_id` is still `required` there
 * whenever no `shipping_method_id` was supplied. Rewriting the constant would
 * have said "a delivery never needs an area", which is false in the mode most
 * of this product runs in. So the table stays the baseline and this function
 * states the ONE substitution retail makes on top of it.
 */
export function requiredFieldsFor(
  orderType: OrderTypeWire,
  pricing: DeliveryPricing,
): readonly OrderField[] {
  const base = ORDER_TYPE_REQUIRED_FIELDS[orderType];
  if (orderType !== 'delivery' || pricing === 'area') return base;
  // A swap, not an addition. Requiring both would reintroduce the hidden
  // prerequisite from the other direction.
  return base.map((field) => (field === 'area_id' ? 'shipping_method_id' : field));
}

/**
 * The forbidden-field row, same shape.
 *
 * `area_id` joins retail's delivery row even though the SERVER does not
 * prohibit it — `'area_id' => [Rule::requiredIf(…), 'nullable', 'integer']`
 * (pos-api OrderController.php:161-168) would accept both keys happily, and
 * OrderCreationService.php:696-716 would then read the area charge and let the
 * method silently overwrite it. That is precisely why the client must not send
 * both: an order carrying two answers to "what does delivery cost" is one whose
 * price cannot be explained from its own payload afterwards. One question, one
 * answer, chosen here rather than resolved by whichever line of PHP runs last.
 */
export function forbiddenFieldsFor(
  orderType: OrderTypeWire,
  pricing: DeliveryPricing,
): readonly OrderField[] {
  const base = ORDER_TYPE_FORBIDDEN_FIELDS[orderType];
  if (orderType !== 'delivery') return base;
  return pricing === 'area' ? [...base, 'shipping_method_id'] : [...base, 'area_id'];
}

/**
 * Reporting labels, not gateway settlement. `payment_method` is a free string
 * server-side; the day-session Z-report buckets cash(+cod)/card/mobile/other
 * (DaySessionController.php:194-199). Bank transfer is OMITTED: the manual
 * proof-verification workflow exists only on the website checkout path, so a
 * counter label would fabricate a workflow V1 cannot honor.
 */
export const PAYMENT_METHOD_LABELS = ['cash', 'card', 'mobile'] as const;
export type PaymentMethodLabel = (typeof PAYMENT_METHOD_LABELS)[number];

export interface OrderItemPayload {
  product_id: number;
  quantity: number;
  /** Persisted verbatim; item notes ride as `attributes.notes`. */
  attributes: Record<string, string>;
  /** STRING index of the parent item within this same items array. */
  parent_key?: string;
}

/**
 * Immutable delivery address snapshot.
 *
 * The website already writes this shape (CheckoutController.php:178-188), so
 * the counter uses the same keys rather than inventing a POS-only one. It is a
 * SNAPSHOT: a delivery record has to stay readable after the guest changes
 * their details, which is also why V2.2 adds no mutable address book.
 */
export interface ShippingAddressPayload {
  street: string;
}

/**
 * The POST /orders body.
 *
 * Which fields are present is decided by `order_type`, and both API layers
 * enforce the same matrix:
 *
 *   dine-in   table_id required; no customer identity, no address
 *   pickup    name and phone OPTIONAL; no table, no address
 *   delivery  name, phone, shipping_address.street and area_id all required
 *
 * A blank pickup is a real walk-in and creates no customer record. That is why
 * `customer_name`/`customer_phone` are optional here — the previous contract
 * required both, which is what pushed counters towards a dummy phone number
 * that collapsed every unrelated walk-in into one customer.
 */
export interface CreateOrderPayload {
  customer_name?: string;
  customer_phone?: string;
  /** Stable selected customer identity for eligibility and order history. */
  customer_id?: number;
  order_type: OrderTypeWire;
  payment_method: PaymentMethodLabel;
  /** External card-machine approval/transaction reference; never card PAN/CVV. */
  payment_reference?: string;
  items: OrderItemPayload[];
  /** Order-level notes — persisted (OrderCreationService.php:77). */
  notes?: string;
  /** Delivery area; drives server-side delivery_charges on delivery orders. */
  area_id?: number;
  /**
   * Retail's answer to the same question — a ShippingZoneMethod id.
   *
   * Mutually exclusive with `area_id` by client policy (`forbiddenFieldsFor`
   * above), and the server prices it LAST: whatever the area said,
   * OrderCreationService.php:702-716 overwrites `delivery_charges` from this
   * method's flat cost or its matching quantity tier. Sending both would leave
   * an order whose postage cannot be read back off its own payload.
   *
   * It must ride on the QUOTE too, or the fingerprint the create is checked
   * against was computed without the postage and every retail Shipping sale
   * 409s by exactly that amount. See `CartStore.refreshQuote`.
   */
  shipping_method_id?: number;
  /** Delivery street snapshot; required by the server on delivery. */
  shipping_address?: ShippingAddressPayload;
  /** Dine-in only. The server refuses a table on any other order type. */
  table_id?: number;
  /**
   * Dine-in head count. A real column upstream since V2.3-02 — it used to be
   * smuggled through `customer_metadata`, where it had no type and could not be
   * corrected afterwards.
   */
  covers?: number;
  day_session_id?: number;
  /** Manual POS discount in rupees; applied after eligible promotions. */
  discount_amount?: number;
  /** Merchant promotion codes; automatic promotions need no client input. */
  promotion_codes?: string[];
  /**
   * Operation id the cart mints once and keeps until an authoritative response
   * arrives. Unique server-side: replaying it returns the original order rather
   * than creating a second one, which is what makes a retry after a lost
   * response safe instead of a guess against the Queue.
   */
  client_order_id?: string;
  /**
   * Fingerprint of the quote the cashier tendered against. If pricing moved
   * since, the server answers 409 with a fresh quote INSTEAD of creating the
   * order — it never charges one amount and banks another.
   */
  quote_fingerprint?: string;
  /** Cash counted out. Change is derived server-side, never sent from here. */
  cash_received?: number;
}

// ---------------------------------------------------------------------------
// Quote — POST {pos}/orders/quote
// ---------------------------------------------------------------------------

/**
 * A read-only price, produced by the same server calculation the create runs.
 *
 * Nothing is written: no order, no customer, no stock movement, no coupon use.
 * The tender screen opens on THIS total so the amount the cashier takes is the
 * amount the order will be recorded with.
 */
export const wireQuoteSchema = z.looseObject({
  subtotal: wireDecimal,
  delivery_charges: wireDecimal,
  discount_amount: wireDecimal,
  total: wireDecimal,
  coupon_code: z.string().nullish(),
  promotion_codes: z.array(z.string()).default([]),
  applied_promotions: z
    .array(
      z.looseObject({
        promotion_id: z.number(),
        title: z.string(),
        code: z.string().nullish(),
        method: z.string().nullish(),
        promotion_type: z.string(),
        merchandise_savings_minor: z.number(),
        shipping_savings_minor: z.number(),
      }),
    )
    .default([]),
  code_results: z
    .array(
      z.looseObject({
        code: z.string(),
        promotion_id: z.number().nullish(),
        state: z.string(),
      }),
    )
    .default([]),
  fingerprint: z.string(),
});
export type WireQuote = z.infer<typeof wireQuoteSchema>;

export const quoteResponseSchema = z.looseObject({
  success: z.literal(true),
  quote: wireQuoteSchema,
});

/** 409 body when the price moved between quoting and charging. */
export const quoteConflictSchema = z.looseObject({
  code: z.string().nullish(),
  error: z.string().nullish(),
  quote: wireQuoteSchema.nullish(),
});

export interface OrderQuote {
  subTotal: DecimalString;
  deliveryCharges: DecimalString;
  discountAmount: DecimalString;
  total: DecimalString;
  fingerprint: string;
  promotionCodes: string[];
  appliedPromotions: Array<{
    id: number;
    title: string;
    code: string | null;
    method: string;
    type: string;
    merchandiseSavingsMinor: number;
    shippingSavingsMinor: number;
  }>;
  codeResults: Array<{ code: string; promotionId: number | null; state: string }>;
}

export function normalizeQuote(wire: WireQuote): OrderQuote {
  return {
    subTotal: wire.subtotal,
    deliveryCharges: wire.delivery_charges,
    discountAmount: wire.discount_amount,
    total: wire.total,
    fingerprint: wire.fingerprint,
    promotionCodes: wire.promotion_codes,
    appliedPromotions: wire.applied_promotions.map((promotion) => ({
      id: promotion.promotion_id,
      title: promotion.title,
      code: promotion.code ?? null,
      method: promotion.method ?? (promotion.code ? 'code' : 'automatic'),
      type: promotion.promotion_type,
      merchandiseSavingsMinor: promotion.merchandise_savings_minor,
      shippingSavingsMinor: promotion.shipping_savings_minor,
    })),
    codeResults: wire.code_results.map((result) => ({
      code: result.code,
      promotionId: result.promotion_id ?? null,
      state: result.state,
    })),
  };
}

// ---------------------------------------------------------------------------
// 201 response — `{ success: true, order, message }`
// ---------------------------------------------------------------------------

export const wireCreatedOrderItemSchema = z.looseObject({
  id: z.number(),
  order_id: z.number().nullish(),
  product_id: z.number(),
  parent_order_item_id: z.number().nullish(),
  quantity: z.number(),
  unit_price: wireDecimal,
  total_price: wireDecimal,
  /** `{}` serializes as `[]` when empty (PHP array quirk); absent entirely is also valid. */
  attributes: z.unknown().optional(),
  product: z
    .looseObject({
      id: z.number(),
      name: z.string(),
      sku: z.string().nullish(),
    })
    .nullish(),
});
export type WireCreatedOrderItem = z.infer<typeof wireCreatedOrderItemSchema>;

const wireFbrFiscalSchema = z.looseObject({
  status: z.string(),
  invoice_number: z.string().nullish(),
  tax_amount: wireDecimal,
  submitted_at: z.string().nullish(),
  error: z.string().nullish(),
});

export const wireCreatedOrderSchema = z.looseObject({
  id: z.number(),
  order_number: z.string(),
  // Nullable now: a dine-in tab has no guest identity and a walk-in pickup may
  // have none either, so the server can legitimately return null here.
  customer_name: z.string().nullish(),
  customer_phone: z.string().nullish(),
  customer_email: z.string().nullish(),
  status: z.string(),
  payment_method: z.string(),
  order_type: z.string(),
  notes: z.string().nullish(),
  area_id: z.number().nullish(),
  table_id: z.number().nullish(),
  /** Loaded on dine-in responses so the receipt can name the table. */
  table: z
    .looseObject({
      id: z.number(),
      label: z.string().nullish(),
    })
    .nullish(),
  customer_metadata: z.unknown().nullish(),
  /**
   * Delivery street snapshot as the server stored it — `{ street: "..." }`.
   * Read back so the printed receipt can carry the address a rider needs; the
   * shape is deliberately loose because it is a stored JSON blob, not a
   * modelled column, and an older row may hold anything.
   */
  shipping_address: z.union([z.record(z.string(), z.unknown()), z.array(z.unknown())]).nullish(),
  delivery_charges: wireDecimal,
  discount_amount: wireDecimal,
  discount_meta: z.unknown().nullish(),
  sub_total: wireDecimal,
  total_amount: wireDecimal,
  day_session_id: z.number().nullish(),
  pos_terminal_id: z.number().nullish(),
  created_at: isoDateTime,
  fiscal: wireFbrFiscalSchema.nullish(),
  items: z.array(wireCreatedOrderItemSchema).default([]),
  area: z
    .looseObject({
      id: z.number(),
      name: z.string(),
      delivery_charges: wireDecimal.optional(),
    })
    .nullish(),
});
export type WireCreatedOrder = z.infer<typeof wireCreatedOrderSchema>;

/**
 * One accepted batch of items — what a kitchen ticket is rendered from.
 *
 * Carries no customer name, phone, address, totals or payment data: a KOT goes
 * to the pass, and the pass has no business holding guest details.
 */
export const wireRoundSchema = z.looseObject({
  id: z.number(),
  sequence: z.number(),
  created_at: z.string().nullish(),
  items: z
    .array(
      z.looseObject({
        id: z.number(),
        product_id: z.number(),
        parent_order_item_id: z.number().nullish(),
        name: z.string().nullish(),
        quantity: z.number(),
        attributes: z.unknown().optional(),
      }),
    )
    .default([]),
});
export type WireRound = z.infer<typeof wireRoundSchema>;

/** Server-computed payment summary; the receipt prints this, not a local sum. */
export const wirePaymentSummarySchema = z.looseObject({
  method: z.string().nullish(),
  total: wireDecimal.nullish(),
  cash_received: wireDecimal.nullish(),
  change_due: wireDecimal.nullish(),
  settled_at: z.string().nullish(),
  reference: z.string().nullish(),
});
export type WirePaymentSummary = z.infer<typeof wirePaymentSummarySchema>;

export const createOrderResponseSchema = z.looseObject({
  success: z.literal(true),
  order: wireCreatedOrderSchema,
  round: wireRoundSchema.nullish(),
  payment: wirePaymentSummarySchema.nullish(),
  message: z.string().nullish(),
});
export type CreateOrderResponse = z.infer<typeof createOrderResponseSchema>;

// ---------------------------------------------------------------------------
// Normalized completed order (receipt/success surface)
// ---------------------------------------------------------------------------

export interface CompletedOrderItem {
  id: number;
  product_id: number;
  name: string;
  sku?: string | null;
  quantity: number;
  unit_price: DecimalString;
  total_price: DecimalString;
  /** Option metadata that round-tripped (string entries, minus `notes`). */
  options: Array<[string, string]>;
  /** Item note extracted from `attributes.notes`. */
  note: string | null;
  addons: CompletedOrderItem[];
}

/**
 * One kitchen round, normalized with addons nested under their parent.
 *
 * A KOT prints exactly this and nothing else. Printing the whole order instead
 * would send round 1 back to the pass every time round 2 arrives.
 */
export interface OrderRound {
  id: number;
  sequence: number;
  createdAt: string | null;
  items: Array<{
    id: number;
    /**
     * The product this line came from — carried so a round can be ROUTED.
     *
     * It was dropped by the normalizer, which meant a round alone could not say
     * what category anything was in, which meant station routing could only be
     * driven from a completed order's items. The dine-in paths do not have one:
     * `openTab` and `addItems` return the round and nothing else. So every
     * kitchen ticket went to the single kitchen default and the category
     * mappings a manager had configured were read by nothing.
     *
     * It is on the wire already (`wireRoundSchema`) and it is not guest data —
     * a product id is the least identifying field on the ticket.
     */
    product_id: number;
    name: string;
    quantity: number;
    /** Option metadata, minus `notes`. */
    options: Array<[string, string]>;
    note: string | null;
    addons: Array<{ id: number; product_id: number; name: string; quantity: number }>;
  }>;
}

/**
 * Does this order's receipt carry the cash figures?
 *
 * NOT ON DELIVERY. The guest is not at the counter — that is the whole reason
 * the order type exists — so the money changes hands at the door, with the
 * rider, after this paper was printed. A counter copy reading "Change Rs 750"
 * describes a handover that has not happened yet, and on a docket the rider
 * carries it reads as money already settled.
 *
 * Pickup and dine-in both settle in front of the till, so their receipt is a
 * record of a payment that really did occur and keeps both lines.
 *
 * The figures are still RECORDED against the order either way; this decides
 * only what the printed copy claims.
 */
export function receiptShowsCashFigures(orderType: string | null | undefined): boolean {
  return orderType !== 'delivery';
}

/** What the receipt prints under the line — every figure server-computed. */
export interface PaymentSummary {
  method: string | null;
  total: DecimalString | null;
  cashReceived: DecimalString | null;
  changeDue: DecimalString | null;
  settledAt: string | null;
  reference?: string | null;
}

export interface FbrFiscalResult {
  status: string;
  invoiceNumber: string | null;
  taxAmount: DecimalString;
  submittedAt: string | null;
  error: string | null;
}

/** The authoritative order used by the success/receipt surfaces. */
export interface CompletedOrder {
  id: number;
  order_number: string;
  customer_name: string;
  customer_phone: string;
  customer_email: string | null;
  status: string;
  payment_method: string;
  order_type: string;
  notes: string | null;
  area_name: string | null;
  /**
   * Delivery street, flattened out of the stored `shipping_address` blob.
   * Null on every other order type, and null when the server did not return
   * one — a receipt prints the line only when there is an address to print.
   */
  street: string | null;
  /** Dine-in only: the table this order was seated at, for the receipt header. */
  table_label: string | null;
  covers: number | null;
  /** Server-computed decimal strings — never recomputed client-side. */
  sub_total: DecimalString;
  delivery_charges: DecimalString;
  discount_amount: DecimalString;
  total_amount: DecimalString;
  day_session_id: number | null;
  created_at: string;
  /** Present only when org-level FBR POS submission is enabled for this sale. */
  fiscal?: FbrFiscalResult | null;
  items: CompletedOrderItem[];
}

function stringEntries(attributes: unknown): Array<[string, string]> {
  if (!attributes || typeof attributes !== 'object' || Array.isArray(attributes)) return [];
  return Object.entries(attributes as Record<string, unknown>).filter(
    (entry): entry is [string, string] => typeof entry[1] === 'string',
  );
}

function normalizeCreatedItem(wire: WireCreatedOrderItem): CompletedOrderItem {
  const entries = stringEntries(wire.attributes);
  const note = entries.find(([key]) => key === 'notes')?.[1] ?? null;
  return {
    id: wire.id,
    product_id: wire.product_id,
    name: wire.product?.name ?? `Product #${wire.product_id}`,
    sku: wire.product?.sku ?? null,
    quantity: wire.quantity,
    unit_price: wire.unit_price,
    total_price: wire.total_price,
    options: entries.filter(([key]) => key !== 'notes'),
    note,
    addons: [],
  };
}

/** Group flat items into parents with nested addons via parent_order_item_id. */
/**
 * The street out of a stored `shipping_address`, or null.
 *
 * Defensive by design: this is a JSON blob the server has written for years,
 * not a typed column, so it may be an object, an array, absent, or hold a
 * non-string under `street`. Anything that is not a non-empty string is
 * treated as "no address" — a receipt that prints `[object Object]` where the
 * rider's address should be is worse than one that prints no line at all.
 */
function streetOf(value: unknown): string | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const street = (value as Record<string, unknown>).street;
  if (typeof street !== 'string') return null;
  const trimmed = street.trim();
  return trimmed === '' ? null : trimmed;
}

export function normalizeCreatedOrder(wire: WireCreatedOrder): CompletedOrder {
  const parents: CompletedOrderItem[] = [];
  const byId = new Map<number, CompletedOrderItem>();
  for (const item of wire.items) {
    if (item.parent_order_item_id == null) {
      const normalized = normalizeCreatedItem(item);
      parents.push(normalized);
      byId.set(item.id, normalized);
    }
  }
  for (const item of wire.items) {
    if (item.parent_order_item_id != null) {
      const normalized = normalizeCreatedItem(item);
      const parent = byId.get(item.parent_order_item_id);
      if (parent) parent.addons.push(normalized);
      else parents.push(normalized); // orphan-safe: never drop a paid line
    }
  }
  const metadata =
    wire.customer_metadata && typeof wire.customer_metadata === 'object'
      ? (wire.customer_metadata as Record<string, unknown>)
      : null;
  const covers = Number(metadata?.covers);

  return {
    id: wire.id,
    order_number: wire.order_number,
    customer_name: wire.customer_name ?? '',
    customer_phone: wire.customer_phone ?? '',
    customer_email: wire.customer_email ?? null,
    status: wire.status,
    payment_method: wire.payment_method,
    order_type: wire.order_type,
    notes: wire.notes ?? null,
    area_name: wire.area?.name ?? null,
    street: streetOf(wire.shipping_address),
    table_label: wire.table?.label ?? null,
    covers: Number.isFinite(covers) && covers > 0 ? covers : null,
    sub_total: wire.sub_total,
    delivery_charges: wire.delivery_charges,
    discount_amount: wire.discount_amount,
    total_amount: wire.total_amount,
    day_session_id: wire.day_session_id ?? null,
    created_at: wire.created_at,
    fiscal: wire.fiscal
      ? {
          status: wire.fiscal.status,
          invoiceNumber: wire.fiscal.invoice_number ?? null,
          taxAmount: wire.fiscal.tax_amount,
          submittedAt: wire.fiscal.submitted_at ?? null,
          error: wire.fiscal.error ?? null,
        }
      : null,
    items: parents,
  };
}

/**
 * Flatten a wire round into printable lines, addons nested under their parent.
 *
 * Same parent/addon grouping as the order normalizer above, but round-local: an
 * addon whose parent belongs to an EARLIER round is kept as its own line rather
 * than dropped, because the kitchen still has to make it.
 */
export function normalizeRound(wire: WireRound): OrderRound {
  type RoundItem = OrderRound['items'][number];
  const parents: RoundItem[] = [];
  const byId = new Map<number, RoundItem>();

  const toLine = (item: WireRound['items'][number]): RoundItem => {
    const entries = stringEntries(item.attributes);
    return {
      id: item.id,
      product_id: item.product_id,
      name: item.name ?? `Product #${item.product_id}`,
      quantity: item.quantity,
      options: entries.filter(([key]) => key !== 'notes'),
      note: entries.find(([key]) => key === 'notes')?.[1] ?? null,
      addons: [],
    };
  };

  for (const item of wire.items) {
    if (item.parent_order_item_id == null) {
      const line = toLine(item);
      parents.push(line);
      byId.set(item.id, line);
    }
  }
  for (const item of wire.items) {
    if (item.parent_order_item_id != null) {
      const line = toLine(item);
      const parent = byId.get(item.parent_order_item_id);
      if (parent) {
        parent.addons.push({
          id: line.id,
          product_id: line.product_id,
          name: line.name,
          quantity: line.quantity,
        });
      } else parents.push(line);
    }
  }

  return {
    id: wire.id,
    sequence: wire.sequence,
    createdAt: wire.created_at ?? null,
    items: parents,
  };
}

export function normalizePaymentSummary(wire: WirePaymentSummary): PaymentSummary {
  return {
    method: wire.method ?? null,
    total: wire.total ?? null,
    cashReceived: wire.cash_received ?? null,
    changeDue: wire.change_due ?? null,
    settledAt: wire.settled_at ?? null,
    reference: wire.reference ?? null,
  };
}

/** Rupee number from a server decimal string ("1650.00" → 1650). */
export function decimalToNumber(value: DecimalString): number {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}
